import React, { createContext, ReactNode, useContext } from "react";

import { AiChatState } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import {
  InputRequirement,
  SurveyQuestion,
} from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import {
  UserInput,
  UserInputAnswer,
} from "@intelligems/ig-types/schemas/ai/node-execution";

import {
  ChatSource,
  DropZoneRef,
  ExecutionPanelSlotProps,
  ExecutionPanelSlots,
  FileUpload,
  Slot,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import { ExecutionPanelProps } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import { Updater } from "use-immer";
import {
  renderToolContent as renderToolContentDefault,
  type ToolBlock,
  type ToolBlockContext,
} from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/toolContent";

/**
 * Derived, panel-scoped state computed once by `ExecutionPanelProvider`. The
 * chat *data* still lives in the `aiChat` redux slice — these values resolve
 * live-vs-historical and the panel's UI state on top of it.
 */
export interface ExecutionPanelDerived {
  aiChat: AiChatState;
  streaming: boolean;
  /**
   * The live chat holds a turn nothing on this page is streaming
   * (`AiChatState.interrupted`). Never true for an injected transcript.
   */
  interrupted: boolean;
  workflowDone: boolean;
  disabled: boolean;
  compact: boolean;
  initialScroll: "smooth" | "instant";
  onSubmit: () => void;
  submitInput: (input: UserInput) => void;
  debugMode: boolean;
  setDebugMode: Updater<boolean>;
  /**
   * The node a surface supplied for `key`, or `defaultComponent` when it
   * supplied none. Generic because not every slot is a node: `toolCard` is a
   * function, since a tool renderer needs the call it draws.
   */
  getSlot: <K extends keyof ExecutionPanelSlots>(
    key: K,
    defaultComponent?: ExecutionPanelSlots[K]
  ) => ExecutionPanelSlots[K];
  /**
   * Props for the default component behind `key`. Returns `undefined` when the
   * caller replaced that slot outright, since their node is already built and
   * these props would silently go nowhere.
   */
  getSlotProps: <K extends keyof ExecutionPanelSlotProps>(
    key: K
  ) => ExecutionPanelSlotProps[K] | undefined;
  /**
   * Draws the sections inside an open tool card, when a surface supplies one.
   *
   * A renderer rather than a slot: it is handed the sections the panel decided
   * apply and answers with what goes inside the card. It may reorder them,
   * merge them, or replace one and hand the rest back to `renderToolBlock`.
   *
   * Which sections apply stays the panel's decision, because it rests on rules
   * a surface should not have to restate: whether the card opens at all, and
   * whether a raw input or result may be shown.
   */
  renderToolContent?: RenderToolContent;
  /**
   * Header overflow-menu state. It lives here rather than in the menu because
   * entries supplied through the `menuItems` slot are built by the consuming
   * surface, outside the menu's tree — `closeMenu` is how they dismiss it.
   */
  menuAnchorEl: HTMLElement | null;
  openMenu: (anchor: HTMLElement) => void;
  closeMenu: () => void;
  inputRequirement: InputRequirement;
  /**
   * Stepper state for a parked multi-question `survey` requirement, `null` for
   * everything else. It lives on the provider rather than in the card because
   * the composer participates: free text typed while a survey is open answers
   * the CURRENT question (see `onSubmit`), which the card alone couldn't know.
   */
  survey: ExecutionPanelSurvey | null;
  userInputMessage: UserInput | null;
  setUserInputMessage: (input: UserInput | null) => void;
  dropZoneRef?: DropZoneRef;
  fileUpload?: FileUpload;
}

export interface ExecutionPanelSurvey {
  questions: SurveyQuestion[];
  currentIndex: number;
  answers: Record<number, UserInputAnswer>;
  goTo: (index: number) => void;
  /**
   * Records the answer to the current question, then auto-advances to the
   * next unanswered question — or, when this was the last unanswered one,
   * submits every answer as ONE `survey` user input.
   */
  answerCurrent: (answer: UserInputAnswer) => void;
  /** Close (X): resume the agent with a declined-to-answer message. */
  dismiss: () => void;
}

/**
 * Everything an `ExecutionPanel` instance exposes downstream: the props passed
 * to `ExecutionPanel` untouched, plus the derived state. Consumers (header,
 * message list, input region) read this instead of being prop-drilled.
 */
export type ExecutionPanelContext = Omit<
  ExecutionPanelProps,
  "slots" | "fileUpload" | "sx" | "chat"
> &
  ExecutionPanelDerived & {
    /** The panel's chat, with the shared one filled in when none was given. */
    chat: ChatSource;
  };

export const ExecutionPanelContextValue =
  createContext<ExecutionPanelContext | null>(null);

export const useExecutionPanelContext = (): ExecutionPanelContext => {
  const ctx = useContext(ExecutionPanelContextValue);
  if (!ctx) {
    throw new Error(
      "useExecutionPanelContext must be used within an ExecutionPanelProvider"
    );
  }
  return ctx;
};

/**
 * Slot props, for a message part that may render outside a panel.
 *
 * The message parts are reused by surfaces that are not an `ExecutionPanel` —
 * `AiStreamedResponse` in the admin trace panel is the live one — and those
 * have no panel to configure them. Throwing there is wrong: the parts only ask
 * what the panel wants them to look like, and "no panel" is a complete answer
 * to that.
 *
 * `undefined` is what `getSlotProps` already returns for a slot the caller
 * replaced, so an absent panel is not a new shape any consumer has to learn.
 *
 * `useExecutionPanelContext` still throws, and everything that needs the
 * panel's state rather than its styling keeps using it.
 */
export const useExecutionPanelSlotProps =
  (): ExecutionPanelContext["getSlotProps"] => {
    const ctx = useContext(ExecutionPanelContextValue);
    return ctx?.getSlotProps ?? (() => undefined);
  };

/** The slots themselves, on the same terms. See `useExecutionPanelSlotProps`. */
/** See `ExecutionPanelDerived.renderToolContent`. */
export type RenderToolContent = (
  blocks: ToolBlock[],
  ctx: ToolBlockContext
) => ReactNode;

/** A surface's tool-content renderer, or the panel's own. */
export const useRenderToolContent = (): RenderToolContent => {
  const ctx = useContext(ExecutionPanelContextValue);
  return ctx?.renderToolContent ?? renderToolContentDefault;
};

export const useExecutionPanelSlot = (): ExecutionPanelContext["getSlot"] => {
  const ctx = useContext(ExecutionPanelContextValue);
  return ctx?.getSlot ?? ((_key, defaultComponent) => defaultComponent);
};

/**
 * A slot's content, given what the panel knows where it draws it.
 *
 * `undefined` means the surface supplied nothing, so the caller draws its own
 * default — and a function returning `undefined` says the same thing, which is
 * how a surface owns some instances of a slot and leaves the rest alone.
 */
export function resolveSlot<A>(
  slot: Slot<A> | undefined,
  args: A
): ReactNode | undefined {
  if (typeof slot === "function") {
    return (slot as (a: A) => ReactNode | undefined)(args);
  }
  return slot;
}
